<template>
    <v-col>
        <!-- Package Details Card -->
        <v-card class="mb-6" outlined>
            <v-card-text
                class="ordinary--text tw-grow tw-flex tw-flex-col tw-gap-4"
            >
                <!-- Package Name and Price -->
                <div
                    class="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between"
                >
                    <div
                        :style="{
                            backgroundColor: `${studio.primaryColor}2e`,
                            color: studio.webAppFontColor,
                        }"
                        class="tw-inline-block tw-rounded-md tw-px-4 tw-py-2"
                    >
                        {{ packageItem.name }}
                    </div>
                    <div class="tw-text-xl tw-font-bold">
                        {{ `${studio.currency}${packageItem.price}` }}
                    </div>
                </div>

                <!-- Credits and Validity -->
                <div class="tw-relative">
                    <div class="tw-text-base">
                        <span class="tw-font-bold">
                            {{ packageItem.classNumbers }}
                            {{ $t('message.credits') }}
                        </span>
                        <span class="tw-ml-2 tw-font-normal">
                            {{ packageDescription }}
                        </span>
                    </div>
                </div>

                <!-- Eligible Classes -->
                <div>
                    <div>
                        {{ $t('message.classesEligible') }}
                    </div>
                    <div class="tw-font-bold">
                        {{ classesRestrictions }}
                    </div>
                </div>

                <!-- Group Booking Eligibility -->
                <div
                    v-if="packageItem.isGroupBookable"
                    class="tw-flex tw-items-center tw-gap-2"
                >
                    <v-icon color="success" small>mdi-account-group</v-icon>
                    <span class="tw-text-green-500">
                        {{
                            $t('message.eligibleForGroupBooking', {
                                pax: packageItem.numGroupBookable,
                            })
                        }}
                    </span>
                </div>

                <div
                    class="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-6 tw-bg-gradient-to-t tw-from-white night:tw-from-black tw-to-transparent"
                ></div>
            </v-card-text>
        </v-card>

        <!-- Account Selection -->
        <div class="tw-relative">
            <div class="tw-mb-4">
                <div
                    class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"
                >
                    <h3 class="tw-text-lg">
                        {{ $t('message.selectAccountToPurchaseFor') }}
                    </h3>
                    <div class="tw-flex">
                        <v-chip
                            v-if="selectedAccounts.length > 0"
                            dark
                            class="tw-mt-1 !tw-whitespace-nowrap !tw-bg-black !tw-text-white sm:tw-mt-0"
                        >
                            <v-icon size="14" class="!tw-mr-1 !tw-text-white">
                                mdi-account-multiple
                            </v-icon>
                            {{
                                $t('message.personSelected', {
                                    count: selectedAccounts.length,
                                })
                            }}
                        </v-chip>
                    </div>
                </div>
            </div>

            <!-- Account list with fixed height -->
            <div class="tw-relative tw-mb-4">
                <div
                    ref="accountList"
                    class="tw-space-y-3 tw-overflow-y-auto tw-pr-2 select-subaccount"
                    :class="{
                        'tw-h-[280px]': !$vuetify.breakpoint.xsOnly,
                        'tw-h-[200px]': $vuetify.breakpoint.xsOnly,
                    }"
                >
                    <div
                        v-for="account in accounts"
                        :key="account.id"
                        class="tw-cursor-pointer tw-rounded-lg tw-p-3 hover:tw-bg-white night:hover:tw-bg-black"
                        @click="toggleAccount(account.id)"
                    >
                        <div class="tw-flex tw-items-center tw-justify-between">
                            <div class="tw-flex tw-items-center tw-gap-4">
                                <avatar :fullname="account.name" />
                                <div>
                                    <div class="tw-font-bold">
                                        {{ account.name }}
                                    </div>
                                    <div class="tw-text-textSecondary">
                                        {{ account.relation }}
                                    </div>
                                </div>
                            </div>
                            <v-checkbox
                                :input-value="isSelected(account.id)"
                                @change="toggleAccount(account.id)"
                                class="tw-mt-0"
                                hide-details
                                @click.stop
                            ></v-checkbox>
                        </div>
                    </div>
                </div>
                <!-- Scroll indicator -->
                <div
                    v-show="hasMoreContent"
                    class="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-2 tw-h-6 tw-bg-gradient-to-t tw-from-white night:tw-from-black tw-to-transparent"
                ></div>
            </div>

            <!-- Action Buttons -->
            <v-row no-gutters class="tw-mt-2 tw-justify-end tw-gap-2">
                <v-btn text @click="emitCancel" class="!tw-uppercase">
                    {{ $t('message.cancel') }}
                </v-btn>
                <v-btn
                    :disabled="selectedAccounts.length === 0"
                    color="primary"
                    @click="emitSuccess"
                    class="!tw-uppercase"
                >
                    {{ $t('message.continue') }}
                </v-btn>
            </v-row>
        </div>
    </v-col>
</template>

<script>
import { mapGetters } from 'vuex'
import {
    STUDIO_MODULE_NAME,
    AUTHENTICATION_MODULE_NAME,
} from '@/store/moduleNames'
import { convertDateFormat } from '@/util/dateformat'
import { getRestrictionList } from '@/util/classes/classFormat'
import Avatar from 'vue-avatar-component'

export default {
    components: {
        Avatar,
    },
    props: {
        familyAccounts: {
            type: Array,
            required: true,
        },
        packageItem: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            selectedAccounts: [],
            hasMoreContent: false,
        }
    },
    computed: {
        ...mapGetters({
            studio: `${STUDIO_MODULE_NAME}/getStudio`,
            user: `${AUTHENTICATION_MODULE_NAME}/getUser`,
        }),
        accounts() {
            const accounts = [this.user, ...this.familyAccounts]
            return accounts.map((account) => ({
                id: account.id,
                name: account.fullName,
                relation: account.relationship
                    ? account.relationship.charAt(0).toUpperCase() +
                      account.relationship.slice(1)
                    : 'Myself',
            }))
        },
        formattedDurationType() {
            const duration =
                this.packageItem.durationOriginal ?? this.packageItem.duration
            const durationType = this.packageItem.durationType ?? 'Days'

            if (duration == 1) {
                return durationType.slice(0, -1).toLowerCase()
            } else {
                return durationType.toLowerCase()
            }
        },
        packageDescription() {
            let criteriaMessage = this.$t('message.firstClass')
            if (
                this.packageItem.expiryImmediateCriterion === 'expiry-immediate'
            ) {
                criteriaMessage = this.$t('message.purchase')
            }
            if (
                this.packageItem.expiryImmediateCriterion === 'expiry-fixed' &&
                this.packageItem.fixedStartDate
            ) {
                criteriaMessage = this.$t('message.fixedDate', {
                    fixedDate: convertDateFormat(
                        this.packageItem.fixedStartDate,
                        'YYYY-MM-DD',
                        'D MMM YYYY'
                    ),
                })
            }
            return this.$t('message.packageValidFor', {
                duration:
                    this.packageItem.durationOriginal ??
                    this.packageItem.duration,
                durationType: this.formattedDurationType,
                criteriaMessage: criteriaMessage,
            })
        },
        classesRestrictions() {
            if (
                this.packageItem.hasClassRestriction &&
                this.packageItem.classes?.length < this.studio.classes.length
            ) {
                return getRestrictionList(
                    this.studio.classes.map((c) => c.className),
                    this.packageItem.classes,
                    this.$t.bind(this)
                )
            }
            return this.$t('message.classesAll')
        },
    },
    mounted() {
        this.checkScroll()
        this.$nextTick(() => {
            const accountList = this.$refs.accountList
            if (accountList) {
                accountList.addEventListener('scroll', this.checkScroll)
            }
        })
    },
    beforeDestroy() {
        const accountList = this.$refs.accountList
        if (accountList) {
            accountList.removeEventListener('scroll', this.checkScroll)
        }
    },
    methods: {
        isSelected(id) {
            return this.selectedAccounts.includes(id)
        },
        toggleAccount(id) {
            const index = this.selectedAccounts.indexOf(id)
            if (index === -1) {
                this.selectedAccounts.push(id)
            } else {
                this.selectedAccounts.splice(index, 1)
            }
        },
        emitSuccess() {
            this.$emit('success', this.selectedAccounts, 'family-account')
        },
        emitCancel() {
            this.$emit('cancel')
        },
        checkScroll() {
            const accountList = this.$refs.accountList
            if (accountList) {
                this.hasMoreContent =
                    accountList.scrollHeight > accountList.clientHeight &&
                    accountList.scrollTop + accountList.clientHeight <
                        accountList.scrollHeight
            }
        },
    },
}
</script>

<style scoped>
.select-subaccount {
    /* Custom scrollbar styles */
    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        @apply tw-bg-gray-100 tw-rounded-lg;
    }

    &::-webkit-scrollbar-thumb {
        @apply tw-bg-gray-400 tw-rounded-lg;
    }

    &::-webkit-scrollbar-thumb:hover {
        @apply tw-bg-gray-500;
    }
}
</style>
